import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { FileTextOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Empty, message, Typography } from 'antd';
import type { TreeProps } from 'antd';
import dayjs from 'dayjs';

import { getFileExtensionSafe } from '@/shared/lib/format';
import { highlightText, matchesKeywordFilter } from '@/shared/lib/text';
import { formatFileSize, getFileType } from '@/shared/lib/utils/fileType';
import { Button } from '@/shared/ui/Button';
import { FileViewer } from '@/shared/ui/FileViewer';
import { Icon } from '@/shared/ui/Icon';
import { SearchInput } from '@/shared/ui/SearchInput';
import { Tree } from '@/shared/ui/Tree';

import { downloadArtifact, getArtifactUrl } from '@/entities/artifact/api/downloadArtifact';
import { downloadModelArtifact } from '@/entities/artifact/api/downloadModelArtifact';
import { useArtifactContent } from '@/entities/artifact/model/useArtifacts';
import type { ArtifactFile, AssetDetailResponse } from '@/entities/asset-search/domain/types';

import shellStyles from '@/widgets/experiment-asset-detail/ExperimentAssetTabShell/ExperimentAssetTabShell.module.css';

import styles from './ArtifactsTab.module.css';

const { Title } = Typography;

const MODEL_ARTIFACT_PREFIX = '/artifacts/';

type TreeDataItem = {
  title: React.ReactNode;
  key: string;
  isLeaf?: boolean;
  children?: TreeDataItem[];
};

type ArtifactsTabProps = {
  artifacts: NonNullable<AssetDetailResponse['artifacts']>;
};

const buildTreeData = (fileList: ArtifactFile[], searchQuery: string = ''): TreeDataItem[] => {
  const renderNodeTitle = (name: string, isLeaf: boolean) => (
    <span className={styles.nodeIconLabel}>
      <Icon name={isLeaf ? 'file' : 'folder'} size={14} />
      <span className={styles.nodeLabel}>
        {searchQuery ? highlightText(name, searchQuery, { className: 'highlight' }) : name}
      </span>
    </span>
  );

  const nodeMap = new Map<string, TreeDataItem>();
  const explicitPaths = new Set(fileList.map((f) => f.path.replace(/\/$/, '')));

  const ensureParentNodes = (path: string) => {
    const parts = path.replace(/\/$/, '').split('/');
    let currentPath = '';

    for (let i = 0; i < parts.length - 1; i++) {
      currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];

      if (!nodeMap.has(currentPath) && !explicitPaths.has(currentPath)) {
        const node: TreeDataItem = {
          title: renderNodeTitle(parts[i], false),
          key: currentPath,
          isLeaf: false,
          children: [],
        };
        nodeMap.set(currentPath, node);
      }
    }
  };

  fileList.forEach((file) => {
    const path = file.path.replace(/\/$/, '');
    const isDir = file.isDir;

    if (!isDir) {
      ensureParentNodes(path);
    }

    const parts = path.split('/');
    const fileName = parts[parts.length - 1];

    const node: TreeDataItem = {
      title: renderNodeTitle(fileName, !isDir),
      key: path,
      isLeaf: !isDir,
      children: isDir ? [] : undefined,
    };

    nodeMap.set(path, node);
  });

  const rootNodes: TreeDataItem[] = [];

  nodeMap.forEach((node, path) => {
    const parts = path.split('/');

    if (parts.length === 1) {
      rootNodes.push(node);
    } else {
      const parentPath = parts.slice(0, -1).join('/');
      const parentNode = nodeMap.get(parentPath);

      if (parentNode && parentNode.children) {
        parentNode.children.push(node);
      }
    }
  });

  return rootNodes;
};

const filterTreeBySearch = (fileList: ArtifactFile[], searchQuery: string): ArtifactFile[] => {
  if (!searchQuery.trim()) return fileList;

  const matchedPaths = new Set<string>();

  fileList.forEach((file) => {
    const pathParts = file.path.split('/');

    const isMatch = pathParts.some((part) => matchesKeywordFilter(searchQuery, part));

    if (isMatch) {
      matchedPaths.add(file.path);

      if (file.isDir) {
        fileList.forEach((f) => {
          if (f.path.startsWith(file.path + '/') || f.path === file.path) {
            matchedPaths.add(f.path);
          }
        });
      }

      // 부모 폴더들도 추가 (트리 구조 유지)
      let currentPath = '';
      pathParts.forEach((part, index) => {
        if (index < pathParts.length - 1) {
          currentPath = currentPath ? `${currentPath}/${part}` : part;
          matchedPaths.add(currentPath);
        }
      });
    }
  });

  return fileList.filter((file) => matchedPaths.has(file.path) || matchedPaths.has(file.path.replace(/\/$/, '')));
};

export const ArtifactsTab: React.FC<ArtifactsTabProps> = ({ artifacts }) => {
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [isDir, setIsDir] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const runUuid = artifacts?.runUuid;

  const processedFiles = useMemo(() => {
    return (artifacts?.files || [])
      .filter((file) => {
        if (file.type === 'model' && !file.path.includes(MODEL_ARTIFACT_PREFIX)) {
          console.warn(`Model artifact without ${MODEL_ARTIFACT_PREFIX} prefix filtered out:`, file.path);
          return false;
        }
        return true;
      })
      .map((file) => {
        if (file.type === 'model') {
          const pathParts = file.path.split(MODEL_ARTIFACT_PREFIX);
          const simplifiedPath = pathParts.length > 1 ? pathParts[pathParts.length - 1] : file.path;
          return {
            ...file,
            path: simplifiedPath,
          };
        }
        return file;
      });
  }, [artifacts]);

  const filteredFiles = useMemo(() => {
    return filterTreeBySearch(processedFiles, searchQuery);
  }, [processedFiles, searchQuery]);

  const treeData = useMemo(() => buildTreeData(filteredFiles, searchQuery), [filteredFiles, searchQuery]);

  useEffect(() => {
    const dirKeys = treeData.filter((node) => !node.isLeaf).map((node) => node.key);
    setExpandedKeys(dirKeys);

    if (treeData.length > 0 && !selectedPath) {
      const firstItem = treeData[0];
      setSelectedPath(firstItem.key);
      setIsDir(!firstItem.isLeaf);
    }
  }, [treeData, selectedPath]);

  useEffect(() => {
    // 검색 시 모든 폴더 확장
    if (searchQuery.trim()) {
      const allDirKeys = treeData.filter((node) => !node.isLeaf).map((node) => node.key);
      const getAllChildKeys = (nodes: TreeDataItem[]): React.Key[] => {
        let keys: React.Key[] = [];
        nodes.forEach((node) => {
          if (!node.isLeaf) {
            keys.push(node.key);
            if (node.children) {
              keys = keys.concat(getAllChildKeys(node.children));
            }
          }
        });
        return keys;
      };
      setExpandedKeys([...allDirKeys, ...getAllChildKeys(treeData)]);
    }
  }, [searchQuery, treeData]);

  const handleSelect: TreeProps['onSelect'] = useCallback(
    (_selectedKeys: React.Key[], info: { selected: boolean; node: { key: React.Key; isLeaf?: boolean } }) => {
      if (info.selected && info.node) {
        setSelectedPath(info.node.key as string);
        setIsDir(!info.node.isLeaf);
      }
    },
    []
  );

  const selectedFile = useMemo(() => {
    if (!selectedPath) return null;
    return processedFiles.find((f) => f.path === selectedPath || f.path.replace(/\/$/, '') === selectedPath) || null;
  }, [selectedPath, processedFiles]);

  const isModelArtifact = selectedFile?.type === 'model';

  const fileType = useMemo(() => {
    if (!selectedPath || isDir) return 'directory';
    return getFileType(selectedPath);
  }, [selectedPath, isDir]);

  const shouldFetchRunContent = !!selectedPath && !isDir && fileType !== 'image' && !isModelArtifact;
  const shouldFetchModelContent =
    !!selectedPath && !isDir && fileType !== 'image' && isModelArtifact && !!selectedFile?.typeId;

  const {
    data: runFileContent,
    isLoading: isLoadingRunContent,
    isError: isRunContentError,
    error: runContentError,
  } = useArtifactContent(runUuid || '', selectedPath || '', shouldFetchRunContent);

  const {
    data: modelFileContent,
    isLoading: isLoadingModelContent,
    isError: isModelContentError,
    error: modelContentError,
  } = useQuery({
    queryKey: ['model-artifact-content', selectedFile?.typeId, selectedPath],
    queryFn: async () => {
      if (!selectedFile?.typeId || !selectedPath) return null;

      const blob = await downloadModelArtifact(selectedFile.typeId, selectedPath);

      if (fileType === 'json') {
        const text = await blob.text();
        return JSON.parse(text);
      }

      if (fileType === 'text') {
        return await blob.text();
      }

      return blob;
    },
    enabled: shouldFetchModelContent,
    staleTime: Infinity,
  });

  const fileContent = isModelArtifact ? modelFileContent : runFileContent;
  const isLoadingContent = isModelArtifact ? isLoadingModelContent : isLoadingRunContent;
  const isContentError = isModelArtifact ? isModelContentError : isRunContentError;
  const contentError = isModelArtifact ? modelContentError : runContentError;

  const previewContent = useMemo(() => {
    if (fileType === 'image' && runUuid && selectedPath && !isModelArtifact) {
      return getArtifactUrl(runUuid, selectedPath);
    }
    return fileContent;
  }, [fileType, runUuid, selectedPath, fileContent, isModelArtifact]);

  const handleDownload = useCallback(async () => {
    if (!selectedPath || isDir || !selectedFile) return;

    try {
      setDownloading(true);

      let blob: Blob;

      if (isModelArtifact && selectedFile.typeId) {
        blob = await downloadModelArtifact(selectedFile.typeId, selectedPath);
      } else if (runUuid) {
        blob = await downloadArtifact(runUuid, selectedPath);
      } else {
        throw new Error('Cannot download: missing runUuid');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = selectedPath.split('/').pop() || 'artifact';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('파일 다운로드가 완료되었습니다.');
    } catch (error) {
      console.error('Failed to download artifact:', error);
      message.error('파일 다운로드에 실패했습니다.');
    } finally {
      setDownloading(false);
    }
  }, [runUuid, selectedPath, isDir, selectedFile, isModelArtifact]);

  if (!artifacts || processedFiles.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="산출물이 없습니다" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        <div className={styles.treeSection}>
          <div className={styles.treeContainer}>
            <div className={styles.header}>
              <Title level={5} className={styles.title}>
                산출물 목록
              </Title>
            </div>
            <div className={styles.searchRow}>
              <SearchInput
                placeholder="검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
                aria-label="검색"
              />
            </div>
            {filteredFiles.length === 0 ? (
              <div className={styles.emptyState}>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="검색 결과가 없습니다" />
              </div>
            ) : (
              <div className={styles.treeWrapper}>
                <Tree
                  treeData={treeData}
                  onSelect={handleSelect}
                  selectedKeys={selectedPath ? [selectedPath] : []}
                  expandedKeys={expandedKeys}
                  onExpand={setExpandedKeys}
                />
              </div>
            )}
          </div>
        </div>
        <div className={styles.viewerSection}>
          {!selectedPath ? (
            <div className={styles.emptyState}>
              <FileTextOutlined className={styles.emptyIcon} />
              <div>미리보기할 파일을 리스트에서 선택해 주세요.</div>
            </div>
          ) : (
            <div className={styles.viewerContainer}>
              <div className={styles.pathHeader}>
                <div className={styles.titleSection}>
                  <div className={styles.fileNameTitle}>{selectedFile?.path.split('/').pop() || selectedPath}</div>
                  {selectedFile && !isDir && (
                    <div className={styles.fileInfoContainer}>
                      <div className={styles.fileInfoItem}>
                        <span className={styles.fileInfoLabel}>형식</span>
                        <span className={styles.fileInfoValue}>{getFileExtensionSafe(selectedFile.path)}</span>
                      </div>
                      <div className={styles.fileInfoDivider} />
                      <div className={styles.fileInfoItem}>
                        <span className={styles.fileInfoLabel}>크기</span>
                        <span className={styles.fileInfoValue}>
                          {selectedFile.fileSize ? formatFileSize(selectedFile.fileSize) : 'N/A'}
                        </span>
                      </div>
                      <div className={styles.fileInfoDivider} />
                      <div className={styles.fileInfoItem}>
                        <span className={styles.fileInfoLabel}>업로드</span>
                        <span className={styles.fileInfoValue}>
                          {artifacts.createdAt ? dayjs(artifacts.createdAt).format('YYYY-MM-DD') : 'N/A'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                {!isContentError && !isDir && (
                  <Button
                    type="button"
                    variant="outlined"
                    className={shellStyles.downloadButton}
                    prefix={<Icon name="download" size={16} />}
                    onClick={handleDownload}
                    disabled={!runUuid || downloading}>
                    다운로드
                  </Button>
                )}
              </div>
              <div className={styles.content}>
                {isDir ? (
                  <div className={styles.emptyState}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="파일을 선택하면 미리보기가 표시됩니다" />
                  </div>
                ) : isContentError ? (
                  <div className={styles.emptyState}>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        contentError instanceof Error && contentError.message.includes('not found')
                          ? '파일을 찾을 수 없습니다'
                          : '파일을 가져오는 중 오류가 발생했습니다'
                      }
                    />
                  </div>
                ) : (
                  <FileViewer
                    embedded
                    path={selectedPath}
                    type={fileType}
                    content={previewContent}
                    isLoading={isLoadingContent}
                    onDownload={handleDownload}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
